import React, { useState, useEffect } from 'react';
import { ruoliAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreHorizontal, 
  Pencil, 
  Trash2, 
  Shield,
  Loader2
} from 'lucide-react';

const initialFormData = {
  nome: '',
  costo_standard: 0,
  tariffa_standard: 0,
  descrizione: ''
};

export const RuoliPage = () => {
  const [ruoli, setRuoli] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingRuolo, setEditingRuolo] = useState(null);
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    loadRuoli();
  }, []);

  const loadRuoli = async () => {
    try {
      const response = await ruoliAPI.getAll();
      setRuoli(response.data);
    } catch (error) {
      console.error('Error loading ruoli:', error);
      toast.error('Errore nel caricamento dei ruoli');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (ruolo = null) => {
    if (ruolo) {
      setEditingRuolo(ruolo);
      setFormData({
        nome: ruolo.nome || '',
        costo_standard: ruolo.costo_standard || 0,
        tariffa_standard: ruolo.tariffa_standard || 0,
        descrizione: ruolo.descrizione || ''
      });
    } else {
      setEditingRuolo(null);
      setFormData(initialFormData);
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingRuolo(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const submitData = {
      ...formData,
      costo_standard: parseFloat(formData.costo_standard) || 0,
      tariffa_standard: parseFloat(formData.tariffa_standard) || 0
    };

    try {
      if (editingRuolo) {
        await ruoliAPI.update(editingRuolo.id, submitData);
        toast.success('Ruolo aggiornato con successo');
      } else {
        await ruoliAPI.create(submitData);
        toast.success('Ruolo creato con successo');
      }
      handleCloseDialog();
      loadRuoli();
    } catch (error) {
      console.error('Error saving ruolo:', error);
      toast.error('Errore nel salvataggio del ruolo');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (ruolo) => {
    if (window.confirm(`Sei sicuro di voler eliminare il ruolo "${ruolo.nome}"?`)) {
      try {
        await ruoliAPI.delete(ruolo.id);
        toast.success('Ruolo eliminato con successo');
        loadRuoli();
      } catch (error) {
        console.error('Error deleting ruolo:', error);
        toast.error('Errore nell\'eliminazione del ruolo');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="ruoli-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ruoli-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Ruoli</h1>
          <p className="text-muted-foreground">Definisci i ruoli e le tariffe standard</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-ruolo-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Ruolo
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome Ruolo</TableHead>
                <TableHead>Descrizione</TableHead>
                <TableHead className="text-right">Costo Standard (€/h)</TableHead>
                <TableHead className="text-right">Tariffa Standard (€/h)</TableHead>
                <TableHead className="text-right">Margine</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ruoli.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">Nessun ruolo definito</p>
                  </TableCell>
                </TableRow>
              ) : (
                ruoli.map((ruolo) => {
                  const margine = ruolo.tariffa_standard - ruolo.costo_standard;
                  const marginePercent = ruolo.tariffa_standard > 0 
                    ? ((margine / ruolo.tariffa_standard) * 100).toFixed(1) 
                    : 0;
                  
                  return (
                    <TableRow key={ruolo.id} data-testid={`ruolo-row-${ruolo.id}`}>
                      <TableCell className="font-medium">{ruolo.nome}</TableCell>
                      <TableCell className="text-muted-foreground">{ruolo.descrizione || '-'}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(ruolo.costo_standard)}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(ruolo.tariffa_standard)}</TableCell>
                      <TableCell className={`text-right font-mono ${margine >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(margine)} ({marginePercent}%)
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`ruolo-actions-${ruolo.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenDialog(ruolo)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(ruolo)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" data-testid="ruolo-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingRuolo ? 'Modifica Ruolo' : 'Nuovo Ruolo'}
            </DialogTitle>
            <DialogDescription>
              {editingRuolo ? 'Modifica i dati del ruolo' : 'Definisci un nuovo ruolo con costi e tariffe'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome Ruolo *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Es. Senior Consultant, Developer..."
                required
                data-testid="ruolo-nome-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="descrizione">Descrizione</Label>
              <Textarea
                id="descrizione"
                value={formData.descrizione}
                onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                rows={2}
                data-testid="ruolo-descrizione-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="costo_standard">Costo Standard (€/h)</Label>
                <Input
                  id="costo_standard"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.costo_standard}
                  onChange={(e) => setFormData({...formData, costo_standard: e.target.value})}
                  data-testid="ruolo-costo-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tariffa_standard">Tariffa Standard (€/h)</Label>
                <Input
                  id="tariffa_standard"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.tariffa_standard}
                  onChange={(e) => setFormData({...formData, tariffa_standard: e.target.value})}
                  data-testid="ruolo-tariffa-input"
                />
              </div>
            </div>
            {formData.tariffa_standard > 0 && (
              <div className="p-3 rounded-lg bg-muted">
                <p className="text-sm text-muted-foreground">
                  Margine calcolato: <span className={`font-mono font-semibold ${
                    (formData.tariffa_standard - formData.costo_standard) >= 0 
                      ? 'text-emerald-600' 
                      : 'text-red-600'
                  }`}>
                    {formatCurrency(formData.tariffa_standard - formData.costo_standard)} 
                    ({((formData.tariffa_standard - formData.costo_standard) / formData.tariffa_standard * 100).toFixed(1)}%)
                  </span>
                </p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Annulla
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="ruolo-submit-btn">
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  editingRuolo ? 'Salva Modifiche' : 'Crea Ruolo'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RuoliPage;
